<template>
  <div class="contract-manage-container">
    <div class="page-header">
      <h2>签约管理</h2>
      <div class="header-actions">
        <el-button v-if="!hasData" type="primary" @click="goCreate">新建进件</el-button>
        <el-button v-if="isDraft || isRejected" type="primary" @click="goCreate">编辑进件</el-button>
        <el-button v-if="isAuditing" type="warning" @click="queryWechatStatus" :loading="querying">查询审核状态</el-button>
        <el-button v-if="hasData" @click="refreshData">刷新状态</el-button>
      </div>
    </div>

    <div v-if="loading" style="text-align:center;padding:100px">
      <i class="el-icon-loading" style="font-size:36px;color:#409eff" />
      <p style="margin-top:15px;color:#999">正在加载...</p>
    </div>

    <template v-if="!loading">
      <el-row :gutter="20">
        <!-- ========== 左侧：微信进件详情 ========== -->
        <el-col :span="8">
          <el-card shadow="never" class="left-card status-card">
            <div class="card-title">进件状态</div>
            <div class="status-area">
              <el-tag v-if="hasData" :type="statusTagType(data.applyStatus)" size="medium">
                {{ statusText(data.applyStatus) }}
              </el-tag>
              <el-tag v-else type="info" size="medium">未申请</el-tag>
            </div>

            <!-- 草稿 -->
            <div v-if="isDraft" class="draft-tip">
              <el-alert title="草稿状态" description="当前进件为草稿，请完善信息后提交审核" type="info" :closable="false" show-icon />
            </div>
            <!-- 已提交 -->
            <div v-if="data && data.applyStatus === 2" class="draft-tip">
              <el-alert title="已提交" description="已提交，等待代理审核" type="warning" :closable="false" show-icon />
            </div>
            <!-- 审核中 -->
            <div v-if="isAuditing" class="draft-tip">
              <el-alert title="微信审核中" description="初审通过，已提交微信支付审核，请耐心等待" type="primary" :closable="false" show-icon />
            </div>
            <!-- 待确认 -->
            <div v-if="isToBeConfirmed" class="draft-tip">
              <el-alert title="待确认" description="微信审核通过，请超级管理员使用微信扫描下方二维码完成账户验证" type="success" :closable="false" show-icon />
              <div v-if="data.signUrl" class="sign-qrcode">
                <el-image :src="data.signUrl" fit="contain" style="width:200px;height:200px;border:1px solid #ebeef5;border-radius:4px" />
                <p class="qrcode-tip">请使用微信扫码完成账户验证</p>
                <el-button type="success" size="small" @click="openSignUrl" icon="el-icon-link">打开验证链接</el-button>
              </div>
            </div>
            <!-- 待签约 -->
            <div v-if="data && data.applyStatus === 4 && data.signState !== 'SIGNED'" class="draft-tip">
              <el-alert title="待签约" description="微信审核已通过，请使用微信扫描下方二维码完成签约" type="success" :closable="false" show-icon />
              <div v-if="data.signUrl" class="sign-qrcode">
                <el-image :src="data.signUrl" fit="contain" style="width:200px;height:200px;border:1px solid #ebeef5;border-radius:4px" />
                <p class="qrcode-tip">请使用微信扫码签约</p>
                <el-button type="success" size="small" @click="openSignUrl" icon="el-icon-link">打开签约链接</el-button>
              </div>
            </div>
            <!-- 已完成 -->
            <div v-if="data && data.applyStatus === 4 && data.signState === 'SIGNED'" class="draft-tip">
              <el-alert title="已完成" description="签约已完成，进件流程结束" type="success" :closable="false" show-icon />
            </div>
            <!-- 已驳回 -->
            <div v-if="isRejected" class="draft-tip">
              <el-alert title="已驳回" :description="data.rejectReason || '审核未通过，请修改后重新提交'" type="danger" :closable="false" show-icon />
            </div>
            <!-- 已撤销 -->
            <div v-if="data && data.applyStatus === 6" class="draft-tip">
              <el-alert title="已撤销" description="该进件申请已撤销" type="info" :closable="false" show-icon />
            </div>
          </el-card>

          <el-card shadow="never" class="left-card">
            <div class="card-title">微信进件详情</div>
            <div class="left-item"><span class="left-label">申请单号</span><span class="left-value">{{ (data && data.applyNo) || '-' }}</span></div>
            <div class="left-item"><span class="left-label">业务编号</span><span class="left-value">{{ (data && data.businessCode) || '-' }}</span></div>
            <div class="left-item"><span class="left-label">微信申请单ID</span><span class="left-value">{{ (data && data.wechatApplyId) || '-' }}</span></div>
            <div class="left-item"><span class="left-label">微信状态</span><span class="left-value">{{ wechatStatusText(data && data.wechatStatus) }}</span></div>
            <div class="left-item"><span class="left-label">微信状态描述</span><span class="left-value">{{ (data && data.wechatStatusDesc) || '-' }}</span></div>
            <div class="left-item"><span class="left-label">微信子商户号</span><span class="left-value">{{ (data && data.wechatSubMerchantId) || '-' }}</span></div>
            <div class="left-item"><span class="left-label">签约状态</span><span class="left-value">{{ signStateText(data && data.signState) }}</span></div>
            <div v-if="data && data.signUrl" class="left-item">
              <span class="left-label">签约链接</span>
              <span class="left-value" style="word-break:break-all">{{ data.signUrl }}</span>
            </div>
            <div class="left-item"><span class="left-label">提交时间</span><span class="left-value">{{ formatTime(data && data.submitTime) }}</span></div>
            <div class="left-item"><span class="left-label">审核时间</span><span class="left-value">{{ formatTime(data && data.auditTime) }}</span></div>
            <div class="left-item"><span class="left-label">签约时间</span><span class="left-value">{{ formatTime(data && data.signTime) }}</span></div>
            <div class="left-item"><span class="left-label">完成时间</span><span class="left-value">{{ formatTime(data && data.completeTime) }}</span></div>

            <div style="margin-top: 20px">
              <el-button v-if="isDraft" type="primary" size="small" @click="editApply" icon="el-icon-edit">编辑进件</el-button>
              <el-button v-if="data && (data.applyStatus === 2 || isAuditing)" type="info" size="small" @click="viewApply" icon="el-icon-view">查看详情</el-button>
              <el-button v-if="data && data.signUrl && data.signState !== 'SIGNED'" type="success" size="small" @click="openSignUrl" icon="el-icon-link">打开签约链接</el-button>
              <el-button v-if="data && (data.applyStatus === 4 || data.applyStatus === 5)" type="info" size="small" @click="viewApply" icon="el-icon-view">查看详情</el-button>
              <el-button v-if="isRejected" type="warning" size="small" @click="editApply" icon="el-icon-edit">修改重新提交</el-button>
            </div>
          </el-card>
        </el-col>

        <!-- ========== 右侧：详细信息 ========== -->
        <el-col :span="16">
          <el-card shadow="never" class="right-card">
            <div class="card-title">基本信息</div>
            <el-row :gutter="16">
              <el-col :span="12"><div class="info-item"><span class="info-label">商户全称</span><span class="info-value">{{ (data && data.merchantName) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">商户简称</span><span class="info-value">{{ (data && data.merchantShortName) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">主体类型</span><span class="info-value">{{ (data && data.subjectType) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">主体类型描述</span><span class="info-value">{{ (data && data.subjectTypeDesc) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">结算规则ID</span><span class="info-value">{{ (data && data.settlementId) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">经营类目</span><span class="info-value">{{ (data && data.industryCategory) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">客服电话</span><span class="info-value">{{ (data && data.servicePhone) || '-' }}</span></div></el-col>
              <el-col :span="24"><div class="info-item"><span class="info-label">经营描述</span><span class="info-value">{{ (data && data.businessDesc) || '-' }}</span></div></el-col>
              <el-col :span="24"><div class="info-item"><span class="info-label">经营场景</span><span class="info-value">{{ salesSceneTypeText(data && data.salesSceneType) }}</span></div></el-col>
            </el-row>
          </el-card>

          <el-card shadow="never" class="right-card">
            <div class="card-title">营业执照信息</div>
            <el-row :gutter="16">
              <el-col :span="12"><div class="info-item"><span class="info-label">统一社会信用代码</span><span class="info-value">{{ (data && data.licenseNumber) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">法人/经营者</span><span class="info-value">{{ (data && data.legalPersonName) || '-' }}</span></div></el-col>
              <el-col :span="24"><div class="info-item"><span class="info-label">注册地址</span><span class="info-value">{{ (data && data.licenseAddress) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">营业执照有效期</span><span class="info-value">{{ formatLicensePeriod(data) }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">是否长期有效</span><span class="info-value">{{ (data && data.licenseIsLongTerm === 1) ? '是' : '否' }}</span></div></el-col>
              <el-col :span="24">
                <div class="info-item">
                  <span class="info-label">营业执照图片</span>
                  <div class="info-value">
                    <div v-if="licenseCopyImageUrl" class="image-preview">
                      <el-image :src="licenseCopyImageUrl" fit="cover" style="width:150px;height:100px;border-radius:4px;cursor:pointer" :preview-src-list="[licenseCopyImageUrl]" lazy preview-teleported />
                    </div>
                    <span v-else>无</span>
                  </div>
                </div>
              </el-col>
            </el-row>
          </el-card>

          <el-card shadow="never" class="right-card">
            <div class="card-title">法人/经营者证件信息</div>
            <el-row :gutter="16">
              <el-col :span="12"><div class="info-item"><span class="info-label">身份证号</span><span class="info-value">{{ (data && data.legalPersonIdCard) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">证件有效期</span><span class="info-value">{{ formatIdCardPeriod(data) }}</span></div></el-col>
              <el-col :span="12">
                <div class="info-item"><span class="info-label">身份证正面</span>
                  <div class="info-value">
                    <div v-if="idCardFrontImageUrl" class="image-preview">
                      <el-image :src="idCardFrontImageUrl" fit="cover" style="width:150px;height:100px;border-radius:4px;cursor:pointer" :preview-src-list="[idCardFrontImageUrl]" lazy preview-teleported />
                    </div>
                    <span v-else>无</span>
                  </div>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="info-item"><span class="info-label">身份证反面</span>
                  <div class="info-value">
                    <div v-if="idCardBackImageUrl" class="image-preview">
                      <el-image :src="idCardBackImageUrl" fit="cover" style="width:150px;height:100px;border-radius:4px;cursor:pointer" :preview-src-list="[idCardBackImageUrl]" lazy preview-teleported />
                    </div>
                    <span v-else>无</span>
                  </div>
                </div>
              </el-col>
            </el-row>
          </el-card>

          <el-card shadow="never" class="right-card">
            <div class="card-title">费率信息</div>
            <el-row :gutter="16">
              <el-col :span="12"><div class="info-item"><span class="info-label">标准费率</span><span class="info-value">{{ formatRate(data && data.standardRate) }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">是否参与优惠</span><span class="info-value">{{ (data && data.hasPromotion === 1) ? '是' : '否' }}</span></div></el-col>
              <el-col :span="12" v-if="data && data.hasPromotion === 1"><div class="info-item"><span class="info-label">优惠活动ID</span><span class="info-value">{{ (data && data.promotionId) || '-' }}</span></div></el-col>
              <el-col :span="12" v-if="data && data.hasPromotion === 1"><div class="info-item"><span class="info-label">优惠费率</span><span class="info-value">{{ formatRate(data && data.promotionRate) }}</span></div></el-col>
              <el-col :span="12" v-if="data && data.hasPromotion === 1"><div class="info-item"><span class="info-label">借记卡费率</span><span class="info-value">{{ formatRate(data && data.debitRate) }}</span></div></el-col>
              <el-col :span="12" v-if="data && data.hasPromotion === 1"><div class="info-item"><span class="info-label">贷记卡费率</span><span class="info-value">{{ formatRate(data && data.creditRate) }}</span></div></el-col>
            </el-row>
          </el-card>

          <el-card shadow="never" class="right-card">
            <div class="card-title">银行账户信息</div>
            <el-row :gutter="16">
              <el-col :span="12"><div class="info-item"><span class="info-label">账户类型</span><span class="info-value">{{ bankAccountTypeText(data && data.bankAccountType) }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">开户银行</span><span class="info-value">{{ (data && data.accountBank) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">开户名称</span><span class="info-value">{{ (data && data.accountName) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">银行账号</span><span class="info-value">{{ maskAccountNumber(data && data.accountNumber) }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">支行名称</span><span class="info-value">{{ (data && data.bankName) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">联行号</span><span class="info-value">{{ (data && data.bankBranchId) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">开户地区编码</span><span class="info-value">{{ (data && data.bankAddressCode) || '-' }}</span></div></el-col>
            </el-row>
          </el-card>

          <el-card shadow="never" class="right-card">
            <div class="card-title">超级管理员信息</div>
            <el-row :gutter="16">
              <el-col :span="12"><div class="info-item"><span class="info-label">管理员类型</span><span class="info-value">{{ adminTypeText(data && data.adminType) }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">姓名</span><span class="info-value">{{ (data && data.adminName) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">手机号</span><span class="info-value">{{ (data && data.adminPhone) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">邮箱</span><span class="info-value">{{ (data && data.adminEmail) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">联系人手机号</span><span class="info-value">{{ (data && data.contactPhone) || '-' }}</span></div></el-col>
              <el-col :span="12"><div class="info-item"><span class="info-label">联系人邮箱</span><span class="info-value">{{ (data && data.contactEmail) || '-' }}</span></div></el-col>
              <el-col :span="24" v-if="data && data.authorizationLetterImageId">
                <div class="info-item"><span class="info-label">授权函</span>
                  <div class="info-value">
                    <div class="image-preview">
                      <el-image :src="authorizationLetterImageUrl" fit="cover" style="width:150px;height:100px;border-radius:4px;cursor:pointer" :preview-src-list="[authorizationLetterImageUrl]" lazy preview-teleported />
                    </div>
                  </div>
                </div>
              </el-col>
            </el-row>
          </el-card>

          <el-card shadow="never" class="right-card">
            <div class="card-title">补充材料</div>
            <el-row :gutter="16">
              <el-col :span="24"><div class="info-item"><span class="info-label">其他资质说明</span><span class="info-value">{{ (data && data.additionDesc) || '-' }}</span></div></el-col>
              <el-col :span="24" v-if="data && data.qualificationImageId">
                <div class="info-item"><span class="info-label">资质图片</span>
                  <div class="info-value">
                    <div class="image-preview">
                      <el-image :src="qualificationImageUrl" fit="cover" style="width:150px;height:100px;border-radius:4px;cursor:pointer" :preview-src-list="[qualificationImageUrl]" lazy preview-teleported />
                    </div>
                  </div>
                </div>
              </el-col>
            </el-row>
          </el-card>
        </el-col>
      </el-row>
    </template>
  </div>
</template>

<script>
import api from '@/utils/api'

export default {
  name: 'ContractManagement',
  data() {
    return {
      loading: false,
      querying: false,
      data: null,
      merchantId: null,
      licenseCopyImageUrl: '',
      idCardFrontImageUrl: '',
      idCardBackImageUrl: '',
      authorizationLetterImageUrl: '',
      qualificationImageUrl: ''
    }
  },
  computed: {
    hasData() { return this.data && this.data.id },
    isDraft() { return this.data && this.data.applyStatus === 1 },
    isRejected() { return this.data && this.data.applyStatus === 5 },
    isAuditing() { return this.data && this.data.applyStatus === 3 },
    isToBeConfirmed() {
      return this.data &&
          this.data.applyStatus === 3 &&
          this.data.wechatStatus === 'APPLYMENT_STATE_TO_BE_CONFIRMED'
    },
    shouldShowQrcode() {
      if (!this.data || !this.data.signUrl) return false
      if (this.data.wechatStatus === 'APPLYMENT_STATE_TO_BE_CONFIRMED') return true
      if (this.data.applyStatus === 4 && this.data.signState !== 'SIGNED') return true
      if (this.data.wechatStatus === 'APPLYMENT_STATE_TO_BE_SIGNED') return true
      if (this.data.wechatStatus === 'APPLYMENT_STATE_SIGNING') return true
      return false
    }
  },
  mounted() { this.loadData() },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const adminRes = await api.login_getAdminInfo()
        const adminData = adminRes.data || adminRes
        if (adminData?.data?.merchantId) {
          this.merchantId = adminData.data.merchantId
        }
        const params = {}
        if (adminData?.data?.merchantId) params.merchantId = adminData.data.merchantId
        if (adminData?.data?.id) params.createAdminId = adminData.data.id
        const res = await api.subMerchant_getMyApply(params)
        if (res?.data?.success) {
          this.data = res.data.data || null
          if (this.data) {
            await this.loadImages()
            // ★★★ 修改：只有状态为3（审核中）或已有微信商户号时才自动同步 ★★★
            await this.autoSyncIfNeeded()
          }
        }
      } catch (e) { console.error('加载失败', e) }
      finally { this.loading = false }
    },

    // ★★★ 修改：自动同步条件 - 状态为3（审核中）或已有微信子商户号 ★★★
    async autoSyncIfNeeded() {
      if (!this.data || !this.data.id || !this.data.wechatApplyId) return

      // 只有状态为3（审核中）或已有微信子商户号时才自动同步
      // 状态2（已提交）是等待代理审核，不应该查微信
      const shouldSync = this.data.applyStatus === 3 || this.data.wechatSubMerchantId
      if (!shouldSync) {
        console.log('当前状态不需要自动同步微信状态')
        return
      }

      await this.queryWechatStatus(true)
    },

    // ★★★ 修改：手动查询 - 所有状态都可以查询 ★★★
    async queryWechatStatus(silent = false) {
      if (!this.data || !this.data.id) return
      this.querying = true
      try {
        const res = await api.subMerchant_queryApplyStatus({
          id: this.data.id,
          wechatApplyId: this.data.wechatApplyId
        })
        if (res?.data?.success && res.data.data) {
          const d = res.data.data
          if (d.applymentState !== undefined) this.data.wechatStatus = d.applymentState
          if (d.applymentStateDesc !== undefined) this.data.wechatStatusDesc = d.applymentStateDesc
          if (d.applyStatus !== undefined && d.applyStatus !== null) this.data.applyStatus = d.applyStatus
          if (d.signState !== undefined && d.signState !== null) this.data.signState = d.signState
          if (d.wechatSubMerchantId) this.data.wechatSubMerchantId = d.wechatSubMerchantId
          if (d.signUrl) this.data.signUrl = d.signUrl
          if (d.rejectReason) this.data.rejectReason = d.rejectReason
          if (!silent) {
            const msgMap = {
              'APPLYMENT_STATE_FINISHED': '审核已通过，签约完成',
              'APPLYMENT_STATE_REJECTED': '审核已驳回',
              'APPLYMENT_STATE_CANCELED': '申请已撤销',
              'APPLYMENT_STATE_TO_BE_SIGNED': '审核通过，待签约',
              'APPLYMENT_STATE_SIGNING': '签约中',
              'APPLYMENT_STATE_TO_BE_CONFIRMED': '待确认，请完成账户验证'
            }
            const msg = msgMap[d.applymentState] || '仍在审核中'
            const type = d.applymentState === 'APPLYMENT_STATE_REJECTED' ? 'warning'
                : d.applymentState === 'APPLYMENT_STATE_FINISHED' || d.applymentState === 'APPLYMENT_STATE_TO_BE_SIGNED' || d.applymentState === 'APPLYMENT_STATE_TO_BE_CONFIRMED' ? 'success'
                    : 'info'
            this.$message({ message: msg, type: type })
          }
        }
      } catch (e) {
        console.error('查询微信状态失败:', e)
        if (!silent) this.$message.error('查询微信状态失败，请稍后重试')
      } finally { this.querying = false }
    },

    async loadImages() {
      const loadImage = async (imageId, setter) => {
        if (!imageId) return
        try {
          const res = await api.image_getImageById({ id: imageId })
          const d = res.data || res
          if (d.code === '000' && d.data) setter(d.data.url || d.data.path)
        } catch (e) { console.error(`加载图片失败 (ID: ${imageId}):`, e) }
      }
      await Promise.all([
        loadImage(this.data.licenseCopyImageId, (url) => { this.licenseCopyImageUrl = url }),
        loadImage(this.data.idCardFrontImageId, (url) => { this.idCardFrontImageUrl = url }),
        loadImage(this.data.idCardBackImageId, (url) => { this.idCardBackImageUrl = url }),
        loadImage(this.data.authorizationLetterImageId, (url) => { this.authorizationLetterImageUrl = url }),
        loadImage(this.data.qualificationImageId, (url) => { this.qualificationImageUrl = url })
      ])
    },

    async refreshData() {
      // 1. 先重新加载本地数据
      await this.loadData()
      // 2. 如果有微信申请单ID，强制从微信查询最新状态
      if (this.data && this.data.id && this.data.wechatApplyId) {
        await this.queryWechatStatus(false)
      }
    },

    goCreate() {
      if (this.hasData && this.data.applyNo) {
        this.$router.push({ path: '/Index/Agent/WxpaySubMerchantApply', query: { applyNo: this.data.applyNo, merchantId: this.data.merchantId, createAdminId: this.data.createAdminId } })
      } else {
        this.$router.push('/Index/Agent/WxpaySubMerchantApply')
      }
    },

    editApply() {
      if (this.data?.applyNo) {
        this.$router.push({ path: '/Index/Agent/WxpaySubMerchantApply', query: { applyNo: this.data.applyNo, merchantId: this.data.merchantId, createAdminId: this.data.createAdminId } })
      }
    },

    viewApply() {
      if (this.data?.applyNo) {
        this.$router.push({ path: '/Index/Agent/WxpaySubMerchantApply', query: { applyNo: this.data.applyNo, merchantId: this.data.merchantId, createAdminId: this.data.createAdminId, readonly: 'true' } })
      }
    },

    openSignUrl() { if (this.data?.signUrl) window.open(this.data.signUrl, '_blank') },

    statusText(s) {
      return { 1:'草稿', 2:'已提交', 3:'审核中', 4:'已通过', 5:'已驳回', 6:'已撤销' }[s] || '未知'
    },
    statusTagType(s) {
      return { 1:'info', 2:'warning', 3:'warning', 4:'success', 5:'danger', 6:'info' }[s] || 'info'
    },

    wechatStatusText(s) {
      if (!s) return '-'
      const m = { 'APPLYMENT_STATE_EDITTING':'编辑中', 'APPLYMENT_STATE_AUDITING':'审核中', 'APPLYMENT_STATE_TO_BE_CONFIRMED':'待确认', 'APPLYMENT_STATE_REJECTED':'已驳回', 'APPLYMENT_STATE_TO_BE_SIGNED':'待签约', 'APPLYMENT_STATE_SIGNING':'签约中', 'APPLYMENT_STATE_FINISHED':'已完成', 'APPLYMENT_STATE_CANCELED':'已撤销' }
      return m[s] ? m[s] : s
    },

    signStateText(s) {
      if (!s) return '-'
      return { 'UNSIGNED':'未签约', 'SIGNING':'签约中', 'SIGNED':'已签约' }[s] || s
    },

    salesSceneTypeText(t) {
      if (!t) return '-'
      return { 'SALES_SCENES_STORE':'线下门店', 'SALES_SCENES_MINI_PROGRAM':'小程序', 'SALES_SCENES_MP':'公众号', 'SALES_SCENES_WEB':'网站', 'SALES_SCENES_APP':'APP' }[t] || t
    },

    bankAccountTypeText(t) {
      if (!t) return '-'
      return t === 'BANK_ACCOUNT_TYPE_CORPORATE' ? '对公账户' : t === 'BANK_ACCOUNT_TYPE_PERSONAL' ? '个人账户' : t
    },
    adminTypeText(t) {
      if (!t) return '-'
      return t === 'LEGAL' ? '法人/经营者' : t === 'SUPERIOR' ? '经办人' : t
    },

    formatRate(rate) { if (!rate && rate !== 0) return '-'; return (parseFloat(rate) * 100).toFixed(2) + '%' },

    formatLicensePeriod(d) {
      const b = this.formatDate(d?.licensePeriodBegin), e = this.formatDate(d?.licensePeriodEnd)
      if (b === '-' && e === '-') return '-'
      if (!d?.licensePeriodEnd || d.licensePeriodEnd === 0) return b + ' 至 长期'
      return b + ' 至 ' + e
    },

    formatIdCardPeriod(d) {
      const b = this.formatDate(d?.idCardPeriodBegin), e = this.formatDate(d?.idCardPeriodEnd)
      if (b === '-' && e === '-') return '-'
      if (!d?.idCardPeriodEnd || d.idCardPeriodEnd === 0) return b + ' 至 长期'
      return b + ' 至 ' + e
    },

    maskAccountNumber(a) { if (!a) return '-'; return a.length <= 8 ? '****' + a.slice(-4) : a.slice(0,4) + '****' + a.slice(-4) },

    formatTime(ts) {
      if (!ts || ts === 0) return '-'
      const d = new Date(parseInt(ts)), p = n => String(n).padStart(2,'0')
      return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
    },

    formatDate(v) {
      if (v === null || v === undefined || v === 0 || v === '' || v === '0') return '-'
      if (typeof v === 'string' && v.includes('T')) return v.substring(0,10)
      if (typeof v === 'number' && v > 0) {
        const d = new Date(v); if (isNaN(d.getTime())) return '-'
        return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
      }
      return '-'
    }
  }
}
</script>

<style scoped>
.contract-manage-container { max-width: 1200px; margin: 0 auto; padding: 20px; }
.page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.page-header h2 { margin: 0; font-size: 22px; font-weight: 600; }
.header-actions { display: flex; gap: 10px; }
.left-card { margin-bottom: 20px; }
.status-card { border-left: 4px solid #409eff; }
.status-area { padding: 20px 0; text-align: center; }
.draft-tip { margin-top: 10px; }
.right-card { margin-bottom: 20px; }
.card-title { font-size: 16px; font-weight: 600; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 1px solid #ebeef5; }
.left-item { padding: 8px 0; border-bottom: 1px dashed #f0f0f0; }
.left-label { display: block; font-size: 12px; color: #909399; margin-bottom: 2px; }
.left-value { display: block; font-size: 14px; color: #303133; word-break: break-all; }
.info-item { padding: 8px 0; border-bottom: 1px dashed #f0f0f0; }
.info-label { display: block; font-size: 13px; color: #909399; margin-bottom: 2px; }
.info-value { display: block; font-size: 14px; color: #303133; }
.image-preview { margin-top: 8px; }
.sign-qrcode { margin-top: 12px; padding: 16px; text-align: center; background: #fff; border-radius: 8px; border: 1px solid #ebeef5; }
.qrcode-tip { margin-top: 8px; font-size: 13px; color: #909399; }
</style>